import CampoTexto from '@/components/base/CampoTexto';
import OpcaoSelecao from '@/components/base/OpcaoSelecao';
import TextoRico from '@/components/base/TextoRico';
import { formatarNascimento, mascaraCPF, mascaraData, mascaraTelefone } from '@/pages/inscricao/utils';
import { normalizarAltura, normalizarPeso, normalizarSenha } from '@/pages/inscricao/ficha/utils';
import CampoLista from './CampoLista';
import type { LinhaLista, PerguntaFicha, ValorResposta } from '../types';

interface CampoPerguntaProps {
  pergunta: PerguntaFicha;
  valor: ValorResposta | undefined;
  erro: string | null;
  onChange: (valor: ValorResposta) => void;
}

// "Nenhum"/"Nenhuma" é exclusivo: marcar limpa os outros e vice-versa.
const NENHUM = ['Nenhum', 'Nenhuma'];

export default function CampoPergunta({ pergunta: p, valor, erro, onChange }: CampoPerguntaProps) {
  const texto = typeof valor === 'string' ? valor : '';
  const lista = Array.isArray(valor) ? valor : [];

  function alternarMultipla(opcao: string) {
    if (NENHUM.includes(opcao)) {
      onChange(lista.includes(opcao) ? [] : [opcao]);
      return;
    }
    const semNenhum = lista.filter((o) => !NENHUM.includes(o));
    const novo = semNenhum.includes(opcao)
      ? semNenhum.filter((o) => o !== opcao)
      : [...semNenhum, opcao];
    onChange(novo);
  }

  const ehSenha = p.chave === 'senha_fila';
  const ehAltura = p.chave === 'altura';
  const ehPeso = p.chave === 'peso';
  const ehMedida = ehAltura || ehPeso;
  const comum = !ehSenha && !ehMedida;

  return (
    <div>
      {ehSenha && (
        <div>
          <input
            id={`ficha-${p.id}`}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={texto}
            onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))}
            onBlur={() => onChange(normalizarSenha(texto))}
            placeholder="Ex.: 01"
            className={`w-full rounded-md border px-3 py-2 text-[15px] text-foreground-950 outline-none transition-colors focus:border-primary-500 ${
              erro ? 'border-danger-500' : 'border-secondary-300'
            }`}
          />

          {p.descricao && (
            <TextoRico
              texto={p.descricao}
              className="mt-1.5 text-xs leading-snug text-secondary-500"
            />
          )}

          {erro && (
            <p className="mt-1 text-sm text-danger-600" role="alert" data-erro="true">
              {erro}
            </p>
          )}
        </div>
      )}

      {ehMedida && (
        <input
          id={`ficha-${p.id}`}
          type="text"
          inputMode="decimal"
          value={texto}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => onChange(ehAltura ? normalizarAltura(texto) : normalizarPeso(texto))}
          placeholder={ehAltura ? 'Ex.: 172' : 'Ex.: 62'}
          className="w-full rounded-md border border-secondary-300 px-3 py-2 text-[15px] text-foreground-950 outline-none transition-colors focus:border-primary-500"
        />
      )}

      {comum && p.tipo === 'texto_curto' && (
        <CampoTexto
          id={`ficha-${p.id}`}
          name={`ficha-${p.id}`}
          label=""
          value={texto}
          onChange={(v) => onChange(v)}
          placeholder="Sua resposta"
        />
      )}

      {comum && p.tipo === 'texto_longo' && (
        <textarea
          id={`ficha-${p.id}`}
          value={texto}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
          placeholder="Sua resposta"
          className="w-full resize-y rounded-md border border-secondary-300 px-3 py-2 text-[15px] text-foreground-950 outline-none transition-colors focus:border-primary-500"
        />
      )}

      {comum && p.tipo === 'sim_nao' && (
        <div className="space-y-1">
          <OpcaoSelecao
            tipo="radio"
            checked={texto === 'true'}
            onChange={() => onChange('true')}
          >
            Sim
          </OpcaoSelecao>
          <OpcaoSelecao
            tipo="radio"
            checked={texto === 'false'}
            onChange={() => onChange('false')}
          >
            Não
          </OpcaoSelecao>
        </div>
      )}

      {comum && p.tipo === 'escolha_unica' && (
        <div className="space-y-1">
          {p.opcoes.map((opcao) => (
            <OpcaoSelecao
              key={opcao}
              tipo="radio"
              checked={texto === opcao}
              onChange={() => onChange(opcao)}
            >
              {opcao}
            </OpcaoSelecao>
          ))}
        </div>
      )}

      {comum && p.tipo === 'multipla_escolha' && (
        <div className="space-y-1">
          {p.opcoes.map((opcao) => (
            <OpcaoSelecao
              key={opcao}
              tipo="checkbox"
              checked={lista.includes(opcao)}
              onChange={() => alternarMultipla(opcao)}
            >
              {opcao}
            </OpcaoSelecao>
          ))}
        </div>
      )}

      {comum && p.tipo === 'numero' && (
        <input
          id={`ficha-${p.id}`}
          type="number"
          inputMode="numeric"
          value={texto}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Sua resposta"
          className="w-full rounded-md border border-secondary-300 px-3 py-2 text-[15px] text-foreground-950 outline-none transition-colors focus:border-primary-500"
        />
      )}

      {comum && p.tipo === 'data' && (
        <input
          id={`ficha-${p.id}`}
          type="text"
          inputMode="numeric"
          value={formatarNascimento(texto)}
          onChange={(e) => onChange(mascaraData(e.target.value))}
          placeholder="DD/MM/AAAA"
          className="w-full rounded-md border border-secondary-300 px-3 py-2 text-[15px] text-foreground-950 outline-none transition-colors focus:border-primary-500"
        />
      )}

      {comum && p.tipo === 'telefone' && (
        <CampoTexto
          id={`ficha-${p.id}`}
          name={`ficha-${p.id}`}
          label=""
          type="tel"
          inputMode="tel"
          value={texto}
          onChange={(v) => onChange(mascaraTelefone(v))}
          placeholder="(00) 00000-0000"
        />
      )}

      {comum && p.tipo === 'email' && (
        <CampoTexto
          id={`ficha-${p.id}`}
          name={`ficha-${p.id}`}
          label=""
          type="email"
          inputMode="email"
          value={texto}
          onChange={(v) => onChange(v)}
          placeholder="maria@gmail.com"
        />
      )}

      {comum && p.tipo === 'cpf' && (
        <CampoTexto
          id={`ficha-${p.id}`}
          name={`ficha-${p.id}`}
          label=""
          inputMode="numeric"
          value={texto}
          onChange={(v) => onChange(mascaraCPF(v))}
          placeholder="000.000.000-00"
        />
      )}

      {comum && p.tipo === 'lista' && (
        <CampoLista
          pergunta={p}
          valor={Array.isArray(valor) ? (valor as LinhaLista[]) : undefined}
          onChange={onChange}
        />
      )}

      {erro && !ehSenha && (
        <p className="mt-1 text-sm text-danger-600" role="alert" data-erro="true">
          {erro}
        </p>
      )}
    </div>
  );
}